/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import styled, { css } from 'styled-components';
import { parseISO } from 'date-fns';
import { formatDate } from 'helpers/intl-helper';
import { FormattedMessage } from 'react-intl';
import DOMPurify from 'dompurify';

import SelectCircle from 'components/shared/DesignSystem/SelectCircle';

// #region styled-components
const ItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
`;

const Photo = styled.img`
  width: 325px;
  height: 410px;
  object-fit: cover;
  border-radius: 8px;
`;

const Wrapper = styled.div`
  position: relative;
  padding: 0px 40px;

  ${(props) =>
    props.isPhoto &&
    css`
      counter-increment: block-counter;

      &::before {
        content: counter(block-counter);
        position: absolute;
        top: 20px;
        left: 60px;
        z-index: 200;

        display: flex;
        align-items: center;
        justify-content: center;

        width: 27px;
        height: 27px;

        background: var(--cc_color_background_1);
        border-radius: 4px;

        color: var(--cc_color_text_default);
        font-weight: 700;
        font-size: 10px;
        line-height: 1;
      }
    `}
`;

const Menu = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  margin: 16px 40px;
`;

const ButtonLabel = styled.span`
  font-size: 14px;
  line-height: 21px;
  font-weight: 600;
  flex: none;
  order: 1;
  flex-grow: 0;
  color: var(--cc_color_text_subtle);
  margin: 0px 0px 0px 6px;
`;

const InsertButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--cc_color_background_1);
  border: 1px solid var(--cc_color_border_default);
  box-sizing: border-box;
  border-radius: 40px;
  padding: 6px 16px 6px 8px;
  color: var(--cc_color_link_subtle);
  transition: 0.2s ease;
  cursor: pointer;
  &:hover {
    color: var(--cc_color_link_primary);
    svg {
      opacity: 1;
    }
    transition: all 0.2s cubic-bezier(0.785, 0.135, 0.15, 0.86) 0s;
    box-shadow: rgb(58 75 86 / 20%) 0px 1px 8px;
  }
`;

const Divider = styled.div`
  display: flex;
  flex: 1;
  height: 1px;
  background: var(--cc_color_border_default);
`;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  padding: 20px;
  flex: 1;
`;

const NoteContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 0px;

  @media (min-width: 1400px) {
    width: 75%;
  }
`;

const MetaContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 0px 0px 0px;

  &:empty {
    display: none;
  }
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: var(--cc_size_spacing_s);
  color: var(--cc_color_text_nonessential);
  font-size: var(--cc_size_text_xxs);
  line-height: 1.25;
  span {
    margin-right: 8px;
    height: 12px;
    width: 12px;
    font-size: var(--cc_size_text_xs);
    color: var(--cc_color_text_subtle);
    align-items: center;
    justify-content: center;
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 40px;
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
`;

const InfoLabel = styled.h3`
  font-size: 16px;
  line-height: 26px;
  font-weight: 500;
  letter-spacing: 0.25px;
  color: var(--cc_color_text_default);
`;

const SublabelWrapper = styled.div`
  max-width: 36rem;
  margin-top: 4px;
  color: var(--cc_color_text_default);
`;

const InfoSublabel = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.25px;
  color: var(--cc_color_text_subtle);
`;
// #endregion styled-components

const InsertPhotosButton = ({ onClick, isSelected }) => (
  <InsertButton
    onClick={onClick}
    className="hint--right"
    aria-label="Insert content here"
  >
    <SelectCircle selected={isSelected} type="button" />
    <ButtonLabel>Insert Here</ButtonLabel>
  </InsertButton>
);

const SectionContent = ({
  sectionNodes,
  setSelectedPosition,
  selectedPosition,
  reportSettings,
}) => {
  const {
    showPhotoCapturedAt,
    showPhotoCreator,
    showPhotoLocation,
    showPhotoTags,
  } = reportSettings;

  useEffect(
    () => {
      if (sectionNodes.length === 0) {
        setSelectedPosition(1);
      }
    },
    [sectionNodes, setSelectedPosition],
  );

  const renderCreateMenu = (position = 0) => {
    return (
      <Menu>
        <Divider />
        <InsertPhotosButton
          type="button"
          onClick={() => setSelectedPosition(position)}
          isSelected={position === selectedPosition}
        />
        <Divider />
      </Menu>
    );
  };

  const renderPhotoMetadata = (entry) => {
    const projectName = get(
      entry,
      'data.reportPhoto.reportProjectInfo.name',
      '',
    );
    const streetAddress = get(
      entry,
      'data.reportPhoto.reportProjectInfo.streetAddress1',
      '',
    );
    const capturedAt = get(entry, 'data.reportPhoto.capturedAt', null);
    const capturer = get(entry, 'data.reportPhoto.contributor.name', '');
    const tags = get(entry, 'data.tags', []);

    return (
      <MetaContainer>
        {showPhotoLocation && (
          <MetaItem>
            <span>
              <i className="mdi mdi-map-marker" />
            </span>
            {projectName || streetAddress}
          </MetaItem>
        )}
        {showPhotoCapturedAt && (
          <MetaItem>
            <span>
              <i className="mdi mdi-calendar" />
            </span>
            {formatDate(parseISO(capturedAt), 'Pp')}
          </MetaItem>
        )}
        {showPhotoCreator && (
          <MetaItem>
            <span>
              <i className="mdi mdi-account" />
            </span>
            {capturer}
          </MetaItem>
        )}
        {showPhotoTags && tags.length > 0 && (
          <MetaItem>
            <span>
              <i className="mdi mdi-tag" />
            </span>
            {tags.map((tag) => tag).join(', ')}
          </MetaItem>
        )}
      </MetaContainer>
    );
  };

  const renderNote = (entry) => {
    const entryText =
      entry.data.text === '' || entry.data.text === null
        ? 'No description added'
        : entry.data.text;
    return (
      <NoteContainer
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entryText) }}
      />
    );
  };

  const renderEntry = (entry, index) => {
    return (
      <React.Fragment key={entry.id}>
        {index === 0 && renderCreateMenu(index)}
        <Wrapper isPhoto>
          <ItemContainer data-entry-id={entry.id}>
            <Photo
              src={get(entry, 'data.reportPhoto.assetPreviewLarge', null)}
              alt="Entry"
            />
            <ContentContainer>
              {renderNote(entry)}
              {renderPhotoMetadata(entry)}
            </ContentContainer>
          </ItemContainer>
        </Wrapper>
        {renderCreateMenu(index + 1)}
      </React.Fragment>
    );
  };

  const EmptyState = () => (
    <EmptyContainer>
      <LabelWrapper>
        <InfoLabel>
          <FormattedMessage
            id="Reports.rearrange.sectionNoContentTitle"
            defaultMessage="This section needs some content!"
            description="Rearrange report section no content title "
          />
        </InfoLabel>
        <SublabelWrapper>
          <InfoSublabel>
            <FormattedMessage
              id="Reports.rearrange.sectionNoContent"
              defaultMessage="Click &ldquo;Confirm and Add Photos&ldquo; to show it a little love. Your photos will be moved to this section in the order they were selected."
              description="Rearrange report section no content message"
            />
          </InfoSublabel>
        </SublabelWrapper>
      </LabelWrapper>
    </EmptyContainer>
  );

  return (
    <>
      {sectionNodes.length > 0 && sectionNodes.map(renderEntry)}
      {sectionNodes.length === 0 && <EmptyState />}
    </>
  );
};

InsertPhotosButton.propTypes = {
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
};

SectionContent.propTypes = {
  sectionNodes: PropTypes.array,
  setSelectedPosition: PropTypes.func,
  selectedPosition: PropTypes.number,
  reportSettings: PropTypes.object,
};

export default SectionContent;
